@if (loading()) {
    <!-- Esqueleto do documento, e não um spinner: o desenho já tem a forma do que está
         chegando (a folha da capa e o cartão do sumário), então a espera mostra o destino
         em vez de um círculo girando. -->
    <div class="preview-skeleton" role="status" aria-live="polite">
        <span class="ra-label-s preview-skeleton-label">
            {{ 'document.preview.loadingDocument' | translate }}
        </span>

        <div class="preview-skeleton-sheet" aria-hidden="true">
            <div class="sk sk-logo"></div>
            <div class="sk sk-title"></div>
            <div class="sk sk-line sk-line--label"></div>
            <div class="sk sk-line"></div>
            <div class="sk sk-line sk-line--short"></div>
            <div class="sk sk-line sk-line--label"></div>
            <div class="sk sk-line"></div>
            <div class="sk sk-line sk-line--short"></div>
        </div>

        <div class="preview-skeleton-card" aria-hidden="true">
            <div class="sk sk-heading"></div>
            <div class="sk sk-line"></div>
            <div class="sk sk-line sk-line--indent"></div>
            <div class="sk sk-line"></div>
            <div class="sk sk-line sk-line--indent"></div>
            <div class="sk sk-line sk-line--short"></div>
        </div>

        <!-- Foto e vídeo: é o que enche um laudo e o que demora a chegar. Reservar a caixa
             deles aqui é o que impede o pulo quando as mídias entram. -->
        <div class="preview-skeleton-card" aria-hidden="true">
            <div class="sk sk-heading"></div>
            <div class="sk sk-line"></div>
            <div class="sk sk-line sk-line--short"></div>

            <div class="preview-skeleton-media">
                <div class="sk sk-media sk-media--image">
                    <span class="sk-media-glyph sk-media-glyph--image"></span>
                </div>
                <div class="sk sk-media sk-media--video">
                    <span class="sk-media-glyph sk-media-glyph--video"></span>
                </div>
            </div>

            <div class="sk sk-line"></div>
        </div>
    </div>
} 
@else if (error()) {
    <div class="container-error">
        <h1>{{ 'document.preview.loadErrorTitle' | translate }}</h1>
    </div>
}
@else {
    @if (!hideMenu()) {

        <!-- Menu -->
        <div class="container-menu">

            <!-- Logo -->
            <div class="container-logo">
                <img src="images/logo.png" alt="RapiDocs" />
                <h1>RapiDocs</h1>
            </div>

            <!-- Document -->
            @if (document())
            {
                <!-- Baixar PDF (documento já gerado) -->
                @if (document()?.pdfStatus == DocGenPdfStatusEnum.COMPLETED)
                {
                    <ra-button
                        [label]="'document.preview.downloadPdf' | translate"
                        [type]="'primary-outline'"
                        (click)="downloadPDF()"
                    />
                }

                <!-- Gerar PDF — a folha pagina num iframe escondido e se imprime sozinha -->
                @else
                {
                    <ra-button
                        [label]="'document.preview.generatePdf' | translate"
                        [type]="'primary'"
                        [loading]="preparingPrint()"
                        [disabled]="preparingPrint()"
                        (click)="requestPDF()"
                    />
                }
            }
        </div>
    }

    @if (document())
    {
        <div class="preview-layout" [class.with-signature]="showSignatureSection() && !hideMenu()">
            <!-- Content -->
            <!-- `notranslate`: o conteúdo do documento não pode ser reescrito por tradutor
                 automático. Além de quebrar o Quill (texto some), traduzir em silêncio o
                 texto de um contrato que a pessoa vai assinar não é aceitável. O menu fica
                 de fora e continua traduzível. -->
            <div
                class="container-content notranslate"
                translate="no"
                [style.--font-family]="previewFontFamily()"
                [style.fontFamily]="previewFontFamily()"
            >
        
                <!-- Cover -->
                @let showCover = document()?.config?.showCover;
                @if ((showCover != false) && document() && coverTemplate()) {
                    <cover [template]="coverTemplate()" />
                }
        
                <!-- Summary -->
                @let showSummary = document()?.config?.showSummary;
                @if ((showSummary != false) && document()?.sessions?.length) {
                    <summary [sessions]="document().sessions"></summary>
                }

                <!-- Area quote -->
                @if (document()?.type == DocumentTypeEnum.QUOTE && document()?.quoteConfig) {
                    <quote
                        [quoteConfig]="document()?.quoteConfig"
                        [currencyCode]="document()?.config?.currencyCode"
                        [locale]="document()?.config?.locale" />
                }
        
                <!-- Sessions -->
                @for (section of document()?.sessions; track $index; let sectionIndex = $index; let last = $last) {
                    <!-- `preview-doc-container`: escopo dos estilos de título/subtítulo/texto
                         do conteúdo do documento (styles/texts.scss).
                         `section-flow`: config `sectionPageBreak: false` — as seções deixam de
                         ser blocos separados e se costuram numa folha só, como no PDF. -->
                    <div class="card-base-design container-section preview-doc-container"
                         [class.section-flow]="sectionsFlow()"
                         [class.section-flow-open]="sectionsFlow() && !last"
                         [style.--line-spacing]="lineSpacing()" [style.--text-size]="textSize()">

                        <!-- O título da seção deixou de vir do `name`: agora é um item TITLE
                             dentro de `items` (renderizado no switch abaixo). -->
                        <div class="container-items-sections">
                            @if (section?.items?.length == 0) {
                                <ra-empty 
                                    [title]="'document.preview.sessionEmptyTitle' | translate" 
                                    [message]="'document.preview.sessionEmptyMessage' | translate" 
                                />
                            }
                            @else 
                            {
                                @for (item of section?.items; track $index; let itemIndex = $index)
                                {
                                    @switch (item?.type)
                                    {
                                        <!-- Title -->
                                        @case (DocumentItemTypeEnum.TITLE)
                                        {
                                            <!-- `id`: alvo do sumário. É o id do ITEM, e não
                                                 um número de ordem, porque é o que o sumário
                                                 já tem em mãos para apontar. -->
                                            <h1 [id]="'doc-item-' + item?._id" class="ra-header-s preview-doc-title preview-anchor" [style.--item-size]="itemTextSize(item)" [style.--item-color]="itemTextColor(item)">
                                                <ra-viewer-text [content]="item?.value" />
                                            </h1>
                                        }

                                        <!-- Subtitle -->
                                        @case (DocumentItemTypeEnum.SUBTITLE)
                                        {
                                            <h2 [id]="'doc-item-' + item?._id" class="preview-doc-subtitle preview-anchor" [style.--item-size]="itemTextSize(item)" [style.--item-color]="itemTextColor(item)">
                                                <ra-viewer-text [content]="item?.value" />
                                            </h2>
                                        }
            
                                        <!-- Text -->
                                        @case (DocumentItemTypeEnum.TEXT) 
                                        {
                                            <p class="preview-doc-text" [style.--item-size]="itemTextSize(item)" [style.--item-color]="itemTextColor(item)">
                                                <ra-viewer-text [content]="item?.value" />
                                            </p>
                                        }
            
                                        @case (DocumentItemTypeEnum.IMAGE) { <image-data [item]="item" [config]="document()?.config" [companyLogo]="document()?.company?.logo" (imageOpen)="openGallery(sectionIndex, itemIndex, $event)" /> }
                                        @case (DocumentItemTypeEnum.GROUP_IMAGE) { <image-data [item]="item" [config]="document()?.config" [companyLogo]="document()?.company?.logo" (imageOpen)="openGallery(sectionIndex, itemIndex, $event)" /> }
                                        @case (DocumentItemTypeEnum.SIGNATURE) { <signature-data [item]="item" /> }
                                        @case (DocumentItemTypeEnum.VIDEO) { <video-data [item]="item" /> }

                                        <!-- Gráfico: o mesmo SVG do PDF -->
                                        @case (DocumentItemTypeEnum.CHART)
                                        {
                                            <chart-view [value]="item?.value" />
                                        }

                                        <!-- Semáforo: mesmos formatos e cores do PDF -->
                                        @case (DocumentItemTypeEnum.SEMAPHORE)
                                        {
                                            <semaphore-view [value]="item?.value" />
                                        }

                                        <!-- Tabela: mesmos estilos e cores do PDF -->
                                        @case (DocumentItemTypeEnum.TABLE)
                                        {
                                            <table-view
                                                [textSize]="itemTextSize(item)"
                                                [value]="item?.value"
                                                [styleKey]="item?.configs?.tableStyle"
                                                [zebra]="item?.configs?.tableZebra"
                                                [color]="item?.configs?.calloutColor" />
                                        }

                                        <!-- Grade de indicadores: mesmos estilos e cores do PDF -->
                                        @case (DocumentItemTypeEnum.STATS)
                                        {
                                            <stats-grid
                                                [value]="item?.value"
                                                [columns]="item?.configs?.statsColumns"
                                                [styleKey]="item?.configs?.statsStyle"
                                                [color]="item?.configs?.calloutColor" />
                                        }

                                        <!-- Caixa de destaque: mesmos pares tinta/fundo do PDF -->
                                        @case (DocumentItemTypeEnum.CALLOUT)
                                        {
                                            @if (item?.name || item?.description) {
                                                @let calloutColor = resolveCalloutColor(item?.configs?.calloutColor);
                                                @let calloutStyle = resolveCalloutStyle(item?.configs?.calloutStyle);
                                                <div class="preview-callout" [class.preview-callout-stripe]="calloutStyle === 'stripe'" [class.preview-callout-outline]="calloutStyle === 'outline'" [class.preview-callout-quote]="calloutStyle === 'quote'" [style.--callout-ink]="calloutColor.ink" [style.--callout-fill]="calloutColor.fill" [style.--callout-accent]="calloutColor.accent" [style.--callout-line]="calloutColor.line" [style.--item-size]="itemTextSize(item)">
                                                    @if (item?.name) {
                                                        <p class="preview-callout-title">{{ item.name }}</p>
                                                    }
                                                    @if (item?.description) {
                                                        <div class="preview-callout-text">
                                                            <ra-viewer-text [content]="item.description" />
                                                        </div>
                                                    }
                                                </div>
                                            }
                                        }

                                    }
                                }
                            }
                        </div>
                    </div>
                }
            </div>

            <!-- Area do botão de assinatura -->
            @let contractSigned = (!signedBy() || signedBy()?.status == DocumentSignedStatusEnum.SIGNED);
            @if (!contractSigned && (showSignatureSection() && !hideMenu())) {
                <aside class="container-signature">
                    <ra-button
                        [label]="'document.preview.sign' | translate"
                        [type]="'primary'"
                        [loading]="false"
                        (click)="signatureFlowOpen.set(true)"
                    />

                    <p class="content-secondary ra-label-xs message-signature">{{ 'document.preview.signatureDisclaimer' | translate }}</p>
                </aside>
            }
        </div>
    }
}

@if (showModalConfirmCode()) {
    <modal-confirm-code
        [showResendSection]="showResendSection()"
        [resendLoading]="resendCodeLoading()"
        [confirmLoading]="confirmCodeLoading()"
        (confirm)="onCodeConfirm($event)"
        (resendCode)="onResendCode($event)" 
    />
}

<!-- Signature flow -->
@if (signatureFlowOpen()) {
    <signature-flow
        (close)="signatureFlowOpen.set(false)"
        (continue)="signatureFlowOpen.set(false); getPreviewData()"
    />
}

<!-- Galeria em tela cheia: uma só para o documento inteiro. Clicar em qualquer foto
     abre nela, mas a navegação percorre TODAS as fotos do documento, atravessando
     cards e seções (antes cada card abria só as próprias fotos). -->
@if (galleryOpen()) {
    <ra-images-full-screen
        [images]="gallery().images"
        [initialIndex]="galleryIndex()"
        [watermark]="galleryWatermark()"
        (close)="galleryOpen.set(false)"
    />
}

<!-- Espera longa da impressão. A folha A4 do desenho pagina sozinha (as linhas de texto
     entram e a página vira), que é literalmente o que está acontecendo por trás. -->
@if (printOverlayOpen()) {
    <div class="print-overlay" role="status" aria-live="polite">
        <div class="print-overlay-card">
            <div class="print-sheet" aria-hidden="true">
                <div class="print-sheet-page">
                    <span class="print-sheet-line print-sheet-line--title"></span>
                    <span class="print-sheet-line"></span>
                    <span class="print-sheet-line"></span>
                    <span class="print-sheet-line print-sheet-line--short"></span>
                    <span class="print-sheet-block"></span>
                    <span class="print-sheet-line"></span>
                    <span class="print-sheet-line print-sheet-line--short"></span>
                </div>
                <div class="print-sheet-page print-sheet-page--next"></div>
            </div>

            <h2 class="ra-header-s print-overlay-title">
                {{ 'document.preview.printingTitle' | translate }}
            </h2>
            <p class="ra-label-m print-overlay-message">
                {{ 'document.preview.printingMessage' | translate }}
            </p>

            <div class="print-overlay-bar"><span></span></div>

            <p class="ra-label-s print-overlay-hint">
                {{ 'document.preview.printingHint' | translate }}
            </p>

            <button type="button" class="print-overlay-cancel ra-label-s" (click)="cancelPrint()">
                {{ 'document.preview.printingCancel' | translate }}
            </button>
        </div>
    </div>
}

<!-- Folha do PDF: fica FORA da tela porque o usuário não deve vê-la — ela existe só para o
     navegador ter o que imprimir. Não usa `display: none` nem `hidden`: iframe sem caixa não
     é renderizado, e o paged.js precisa medir alturas reais para paginar. -->
@if (printFrameUrl(); as url) {
    <iframe
        #printFrame
        title="pdf"
        [src]="url"
        aria-hidden="true"
        tabindex="-1"
        style="position: fixed; left: -10000px; top: 0; width: 794px; height: 1123px; border: 0"
    ></iframe>
}
